import * as React from "react";
import * as ReactDOM from "react-dom";
import clsx from "clsx";
import { Transition } from "react-transition-group";
import { CloseButton } from "../../close-button/close-button.mjs";
import { HashTrigger } from "../../hash-trigger/hash-trigger.mjs";
import debounce from "debounce";
import "tabbable";
import { VisibilityProvider } from "../../helpers/visibility-provider/visibility-provider.mjs";
import { CollapseContext } from "../collapse-provider/collapse-provider.mjs";
const Collapse = (props) => {
  const { isVisible, id, portal, trigger, onClose, onOpen, onRest } = React.useContext(CollapseContext);
  return /* @__PURE__ */ React.createElement(CollapseInner, { ...props, isVisible, id, portal, trigger, onClose, onOpen, onRest });
};
class CollapseInner extends React.Component {
  constructor(props) {
    super(props);
    this.elementRef = React.createRef();
    this.expandTimeout = 0;
    this.handleTransitionEnd = (event) => {
      if (event.target === this.elementRef.current && event.propertyName === "height") {
        if (this.props.isVisible) {
          this.setExpanded();
        } else {
          this.setCollapsed();
        }
        this.props.onRest();
      }
    };
    this.handleCloseClick = (event) => {
      event.preventDefault();
      this.props.onClose();
      if (this.props.onCloseClick) {
        this.props.onCloseClick();
      }
    };
    this.handleHashMatch = () => {
      if (!this.props.isVisible) {
        this.props.onOpen();
      }
    };
    this.renderContent = (state) => {
      const isInert = state !== "entered" && !this.props.isVisible && !this.props.initialVisibleHeight ? "" : void 0;
      return /* @__PURE__ */ React.createElement(HashTrigger, { id: this.props.id, onMatch: this.handleHashMatch, renderWrapper: false }, /* @__PURE__ */ React.createElement(
        "div",
        {
          className: clsx(
            "collapse",
            "is-" + state,
            {
              ["collapse--".concat(this.props.color)]: !!this.props.color,
              "collapse--no-shadow": !!this.props.noShadow,
              "collapse--no-arrow": !!this.props.noArrow,
              "collapse--no-container": !!this.props.noContainer,
              "collapse--no-close": !!this.props.noClose,
              "collapse--fade": !!this.props.initialVisibleHeight
            },
            this.props.className
          ),
          ref: this.elementRef,
          style: this.state.collapseStyle,
          onTransitionEnd: this.handleTransitionEnd,
          ...{ inert: isInert }
        },
        this.renderArrow(),
        /* @__PURE__ */ React.createElement("div", { className: "collapse__inner", id: this.props.id }, this.renderCloseButton(), this.props.children)
      ));
    };
    const closedCollapseStyle = {
      height: "0px",
      overflow: "hidden",
      visibility: "hidden"
    };
    const fadedCollapseStyle = {
      height: "".concat(props.initialVisibleHeight, "px"),
      overflow: "hidden",
      visibility: "visible"
    };
    this.state = {
      arrowStyle: {},
      collapseStyle: props.initialVisibleHeight ? fadedCollapseStyle : closedCollapseStyle,
      isAnimating: false,
      isMounted: false
    };
    this.debouncedResizeHandler = debounce(this.setArrowPosition.bind(this), 100);
  }
  componentDidMount() {
    this.setState(
      {
        isMounted: true
      },
      () => {
        if (this.elementRef.current && this.props.isVisible) {
          this.setExpanded();
          this.setArrowPosition();
        }
      }
    );
    window.addEventListener("resize", this.debouncedResizeHandler);
  }
  componentDidUpdate(prevProps, prevState) {
    if (this.state.collapseStyle !== prevState.collapseStyle) {
      this.setArrowPosition();
    }
    if (this.props.isVisible && !prevProps.isVisible) {
      this.expand();
    } else if (!this.props.isVisible && prevProps.isVisible) {
      this.collapse();
    }
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedResizeHandler);
    this.debouncedResizeHandler.clear();
    if (this.expandTimeout) {
      window.clearTimeout(this.expandTimeout);
    }
  }
  expand() {
    this.setState({
      isAnimating: true
    });
    window.clearTimeout(this.expandTimeout);
    this.expandTimeout = window.setTimeout(() => {
      if (!this.elementRef.current) {
        return;
      }
      this.setContentStyleProperty("height", "".concat(this.elementRef.current.scrollHeight, "px"));
      this.setContentStyleProperty("visibility", "visible");
    }, 0);
  }
  collapse() {
    if (!this.elementRef.current) {
      return;
    }
    this.setState({
      isAnimating: true
    });
    this.setContentStyleProperty("height", "".concat(this.elementRef.current.scrollHeight, "px"));
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.setContentStyleProperty("height", this.props.initialVisibleHeight ? "".concat(this.props.initialVisibleHeight, "px") : "0px");
        this.setContentStyleProperty("overflow", "hidden");
      }, 0);
    });
  }
  setContentStyleProperty(property, value) {
    this.setState((prevState) => {
      return {
        collapseStyle: {
          ...prevState.collapseStyle,
          [property]: value
        }
      };
    });
  }
  setCollapsed() {
    this.setContentStyleProperty("visibility", this.props.initialVisibleHeight ? "visible" : "hidden");
    this.setState({
      isAnimating: false
    });
  }
  setExpanded() {
    window.clearTimeout(this.expandTimeout);
    this.setContentStyleProperty("height", "auto");
    this.setContentStyleProperty("overflow", "visible");
    this.setContentStyleProperty("visibility", "visible");
    this.setArrowPosition();
    this.setState({
      isAnimating: false
    });
  }
  setArrowPosition() {
    if (this.props.isVisible && this.props.trigger && this.elementRef.current) {
      const buttonRect = this.props.trigger.getBoundingClientRect();
      const collapseRect = this.elementRef.current.getBoundingClientRect();
      const arrowPosition = buttonRect.left - collapseRect.left + buttonRect.width / 2 - 8;
      this.setState({
        arrowStyle: {
          left: arrowPosition + "px"
        }
      });
    }
  }
  hasArrow() {
    return !this.props.noArrow && !this.props.noContainer;
  }
  renderArrow() {
    if (this.hasArrow()) {
      return /* @__PURE__ */ React.createElement("div", { className: "collapse__arrow", style: this.state.arrowStyle });
    }
    return null;
  }
  renderCloseButton() {
    const { closeButtonLabel, noClose, noContainer, isVisible } = this.props;
    if (noClose || noContainer || !isVisible) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(CloseButton, { onClick: this.handleCloseClick, label: closeButtonLabel, className: "collapse__close" });
  }
  renderCollapse() {
    return /* @__PURE__ */ React.createElement(VisibilityProvider, { isVisible: this.props.isVisible && !this.state.isAnimating || false }, /* @__PURE__ */ React.createElement(Transition, { in: this.props.isVisible, timeout: { enter: 300, exit: 300 }, nodeRef: this.elementRef }, this.renderContent));
  }
  render() {
    if (this.state.isMounted && this.props.portal) {
      return ReactDOM.createPortal(this.renderCollapse(), this.props.portal);
    }
    return this.renderCollapse();
  }
}
export {
  Collapse,
  CollapseInner
};
